/**
 * Servicio de gestión del iframe de PinPad Web v2.6.0
 * 
 * Responsabilidades:
 * - Crear y destruir el iframe
 * - Verificar carga exitosa
 * - Gestionar ciclo de vida
 * - Proveer acceso al contentWindow
 * 
 * @module pinpad-v2/iframe-service
 */

import { getPinpadUrl } from '../../config/pinpad-v2/urls'
import { logInfo, logError, logWarn } from '../../utilities/pinpad-v2/logger'

class IframeService {
  constructor() {
    this.iframe = null
    this.isReady = false
    this.readyCallbacks = []
    this.loadTimeout = null
    this.LOAD_TIMEOUT_MS = 30000 // 30 segundos
  }

  /**
   * Crea el iframe de PinPad Web
   * @param {string} environment - 'QA' o 'PROD'
   * @returns {Promise<HTMLIFrameElement>}
   */
  createIframe(environment = 'QA') {
    return new Promise((resolve, reject) => {
      if (this.iframe) {
        logInfo('Iframe already exists, returning existing instance', {
          isReady: this.isReady,
          src: this.iframe.src
        })
        
        if (this.isReady) {
          resolve(this.iframe)
        } else {
          this.onReady(() => resolve(this.iframe))
        }
        return
      }

      const url = getPinpadUrl(environment)
      
      logInfo('Creating PinPad Web iframe', { url, environment })

      this.iframe = document.createElement('iframe')
      this.iframe.src = url
      this.iframe.style.display = 'none'
      this.iframe.style.width = '0'
      this.iframe.style.height = '0'
      this.iframe.style.border = 'none'
      this.iframe.id = 'pinpad-web-iframe'
      this.iframe.title = 'PinPad Web v2.6.0'
      
      // Agregar atributos de seguridad para iframe
      this.iframe.setAttribute('sandbox', 'allow-same-origin allow-scripts allow-forms')
      this.iframe.setAttribute('allow', 'serial;geolocation;')
      
      // Timeout de carga
      this.loadTimeout = setTimeout(() => {
        const error = new Error(`Iframe load timeout after ${this.LOAD_TIMEOUT_MS}ms`)
        logError('Iframe load timeout', error)
        this.cleanup()
        reject(error)
      }, this.LOAD_TIMEOUT_MS)

      // Listener para detectar cuando el iframe está listo
      this.iframe.onload = () => {
        clearTimeout(this.loadTimeout)
        this.isReady = true
        
        logInfo('PinPad Web iframe loaded successfully', {
          url,
          readyCallbacks: this.readyCallbacks.length
        })
        
        // Ejecutar callbacks pendientes
        this.readyCallbacks.forEach(cb => {
          try {
            cb()
          } catch (error) {
            logError('Error executing ready callback', error)
          }
        })
        this.readyCallbacks = []
        
        resolve(this.iframe)
      }

      this.iframe.onerror = (error) => {
        clearTimeout(this.loadTimeout)
        logError('Failed to load PinPad Web iframe', error)
        this.cleanup()
        reject(error)
      }

      // Agregar al DOM
      try {
        document.body.appendChild(this.iframe)
      } catch (error) {
        clearTimeout(this.loadTimeout)
        logError('Failed to append iframe to DOM', error)
        this.cleanup()
        reject(error)
      }
    })
  }

  /**
   * Obtiene el contentWindow del iframe
   * @returns {Window}
   * @throws {Error} Si el iframe no está creado
   */
  getContentWindow() {
    if (!this.iframe) {
      throw new Error('Iframe not created. Call createIframe() first.')
    }
    
    if (!this.iframe.contentWindow) {
      throw new Error('Iframe contentWindow not available')
    }
    
    return this.iframe.contentWindow
  }

  /**
   * Ejecuta callback cuando el iframe esté listo
   * @param {Function} callback
   */
  onReady(callback) {
    if (!callback || typeof callback !== 'function') {
      logWarn('onReady called with invalid callback')
      return
    }

    if (this.isReady) {
      try {
        callback()
      } catch (error) {
        logError('Error executing immediate ready callback', error)
      }
    } else {
      this.readyCallbacks.push(callback)
    }
  }

  /**
   * Limpia recursos internos
   * @private
   */
  cleanup() {
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout)
      this.loadTimeout = null
    }
    
    this.isReady = false
    this.readyCallbacks = []
  }

  /**
   * Destruye el iframe completamente
   */
  destroy() {
    if (this.iframe) {
      logInfo('Destroying PinPad Web iframe', {
        src: this.iframe.src,
        isReady: this.isReady
      })
      
      try {
        if (this.iframe.parentNode) {
          this.iframe.parentNode.removeChild(this.iframe)
        }
      } catch (error) {
        logError('Error removing iframe from DOM', error)
      }
      
      this.iframe = null
    }
    
    this.cleanup()
  }

  /**
   * Verifica si el iframe existe
   * @returns {boolean}
   */
  exists() {
    return this.iframe !== null
  }

  /**
   * Verifica si el iframe está listo para comunicación
   * @returns {boolean}
   */
  ready() {
    return this.isReady && this.iframe !== null
  }

  /**
   * Reinicia el iframe (destruye y crea uno nuevo)
   * @param {string} environment
   * @returns {Promise<HTMLIFrameElement>}
   */
  async restart(environment = 'QA') {
    logInfo('Restarting iframe', { environment })
    this.destroy()
    return this.createIframe(environment)
  }
}

// Singleton
const iframeService = new IframeService()

export default iframeService
